import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { TrendingUp, TrendingDown, Wallet, BarChart3, Landmark, Clock } from 'lucide-react';
import AdminInvoicesInbox from '@/components/AdminInvoicesInbox';
import ProjectRevenuePanel from '@/components/ProjectRevenuePanel';
import TdsLedgerPanel from '@/components/TdsLedgerPanel';
import CaPackDownload from '@/components/CaPackDownload';

const fmtL = v => `${(Number(v || 0) / 100000).toFixed(1)}L`;
const fmt = v => `\u20B9${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function AccountantDashboard() {
  const { api } = useAuth();
  const [overview, setOverview] = useState(null);

  const load = useCallback(() => {
    api.get('/accounting/overview').then(r => setOverview(r.data)).catch(console.error);
  }, [api]);
  useEffect(load, [load]);

  if (!overview) return <div className="flex items-center justify-center h-64"><div className="animate-pulse-slow text-zinc-600 text-sm">Loading...</div></div>;

  const util = overview.total_budget > 0 ? Math.round(overview.total_spent / overview.total_budget * 100) : 0;

  return (
    <div className="space-y-6" data-testid="accountant-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Financial Overview</h1>
          <p className="text-xs text-zinc-500 mt-1">Live studio books — project value, money received, vendor payouts and TDS</p>
        </div>
        <CaPackDownload />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Tile testid="tile-received" icon={<TrendingUp className="w-4 h-4 text-emerald-400" />} label="Received from Clients"
          value={fmtL(overview.total_received)} color="text-emerald-400" sub={`${overview.receipt_count} receipt${overview.receipt_count === 1 ? '' : 's'} punched`} />
        <Tile testid="tile-project-value" icon={<BarChart3 className="w-4 h-4 text-violet-400" />} label="Total Project Value"
          value={fmtL(overview.total_project_value)} color="text-violet-400"
          sub={`${overview.active_projects} active${overview.delivered_projects ? ` · ${overview.delivered_projects} delivered` : ''}`} />
        <Tile testid="tile-pending-clients" icon={<Clock className="w-4 h-4 text-amber-400" />} label="Pending from Clients"
          value={fmtL(overview.pending_from_clients)} color="text-amber-400" sub="value − received" />
        <Tile testid="tile-payouts" icon={<TrendingDown className="w-4 h-4 text-red-400" />} label="Vendor Payouts"
          value={fmtL(overview.total_payouts)} color="text-red-400" sub={`${fmt(overview.payables_open)} still open`} />
        <Tile testid="tile-net-balance" icon={<Wallet className="w-4 h-4 text-blue-400" />} label="Net Balance"
          value={fmtL(overview.net_balance)} color="text-blue-400" sub="received − paid out" />
        <Tile testid="tile-tds" icon={<Landmark className="w-4 h-4 text-rose-400" />} label="TDS to Deposit"
          value={fmt(overview.tds_to_deposit)} color="text-rose-400" sub={`${fmt(overview.tds_upcoming)} on open invoices`} small />
      </div>

      {/* Payouts against contracted value */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5" data-testid="budget-utilization">
        <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-4">Payouts vs Contracted Value</h3>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
              <span>Paid to vendors: {fmt(overview.total_spent)}</span>
              <span>Contracted value: {fmt(overview.total_budget)}</span>
            </div>
            <div className="w-full h-4 bg-zinc-800 rounded-sm overflow-hidden">
              <div className="h-full bg-blue-500 rounded-sm transition-all" style={{ width: `${Math.min(util, 100)}%` }} />
            </div>
          </div>
          <span className="text-lg font-heading font-bold text-white">{util}%</span>
        </div>
        {overview.projects_missing_value > 0 && (
          <p className="text-[10px] text-amber-400/80 mt-2" data-testid="value-missing-hint">
            {overview.projects_missing_value} active project{overview.projects_missing_value > 1 ? 's' : ''} still have no contract value — set it in the Client Revenue Ledger below.
          </p>
        )}
      </div>

      {/* Client money: project value vs received vs pending */}
      <ProjectRevenuePanel onChange={load} />

      {/* TDS pendency — vendor-wise / project-wise, quarter by quarter */}
      <TdsLedgerPanel />

      {/* Invoices Inbox — vendors and freelancers raise; admin reviews + pays */}
      <AdminInvoicesInbox />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm" data-testid="recent-receipts-card">
          <div className="px-4 py-3 border-b border-zinc-800">
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Recent Money Received</h3>
          </div>
          <div className="divide-y divide-zinc-800/30">
            {(overview.recent_receipts || []).map(r => (
              <div key={r.id} className="px-4 py-2.5 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-mono text-emerald-400">+{fmt(r.amount)}</p>
                  <p className="text-[10px] text-zinc-600 truncate">{r.project_name} · {r.mode}{r.reference ? ` · ${r.reference}` : ''}</p>
                </div>
                <span className="text-[9px] text-zinc-500 font-mono">{r.received_on}</span>
              </div>
            ))}
            {(overview.recent_receipts || []).length === 0 && <p className="text-xs text-zinc-600 px-4 py-6 text-center">No client payments recorded yet — punch them in the ledger above</p>}
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-sm" data-testid="recent-payouts-card">
          <div className="px-4 py-3 border-b border-zinc-800">
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Recent Vendor Payouts</h3>
          </div>
          <div className="divide-y divide-zinc-800/30">
            {(overview.recent_payouts || []).map(p => (
              <div key={p.id} className="px-4 py-2.5 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-mono text-red-400">-{fmt(p.amount)}</p>
                  <p className="text-[10px] text-zinc-600 truncate">{p.vendor} · {p.invoice_no}{p.payment_ref ? ` · ${p.payment_ref}` : ''}</p>
                </div>
                <span className="text-[9px] text-zinc-500 font-mono">{(p.paid_at || '').slice(0, 10)}</span>
              </div>
            ))}
            {(overview.recent_payouts || []).length === 0 && <p className="text-xs text-zinc-600 px-4 py-6 text-center">No vendor invoices marked paid yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon, label, value, color, sub, testid, small }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">{icon}<span className="text-[10px] text-zinc-500 uppercase">{label}</span></div>
      <p className={`${small ? 'text-lg' : 'text-2xl'} font-heading font-bold ${color} font-mono`}>{value}</p>
      {sub && <p className="text-[9px] text-zinc-600 mt-1 truncate">{sub}</p>}
    </div>
  );
}
